import { FormSchemaDropdownInput } from "#src/batteries-included-components/Dropdowns/FormSchemaDropdownInput";
import {
  Banner,
  Card,
  CheckboxInput,
  Form,
  KeyValueList,
  useForm,
} from "@validereinc/common-components";
import { FormSubmissionTaskType } from "@validereinc/domain";
import classNames from "classnames/bind";
import React, { useEffect } from "react";
import { useVerifyNewFormSchema } from "../Utils/useVerifyNewFormSchema";
import {
  useWorkflowConfigurationFormStore,
  type SubmitFormFormValuesType,
} from "../WorkflowConfigurationFormStore";
import styles from "./WorkflowConfigurationSubmitForm.module.scss";

const cx = classNames.bind(styles);

const WorkflowConfigurationSubmitForm = ({
  taskDefinition,
}: {
  taskDefinition: FormSubmissionTaskType;
}) => {
  const { setSubmitForm, setSubmitFormIsValid, getSubmitForm } =
    useWorkflowConfigurationFormStore();

  const storeValues = getSubmitForm();

  const form = useForm<SubmitFormFormValuesType>({
    values: storeValues,
  });

  const { isVerifying, results } = useVerifyNewFormSchema({
    output: taskDefinition.output,
    input: taskDefinition.input,
    currentFormSchemaId: taskDefinition.form_schema_id,
    selectedFormSchemaId: storeValues?.form_schema_id,
  });

  useEffect(() => {
    const isValid =
      form.formState.isValid && !isVerifying && results.length === 0;
    setSubmitFormIsValid(isValid);
  }, [
    form.formState.isValid,
    isVerifying,
    results.length,
    setSubmitFormIsValid,
  ]);

  const handleValueChange = (
    key: "form_schema_id" | "form_required" | "autocomplete" | "auto_attach",
    value: unknown
  ) => {
    if (storeValues) {
      setSubmitForm({
        ...storeValues,
        [key]: value,
      });
    }
  };

  return (
    <>
      <Form {...form}>
        <FormSchemaDropdownInput
          name="form_schema_id"
          label="Form Template"
          isMulti={false}
          fetchFilters={{ status: "active" }}
          isRequired
          onChange={(formSchemaId: string) => {
            handleValueChange("form_schema_id", formSchemaId);
          }}
        />

        <div className={cx("submission-control")}>
          <div className={cx("submission-control-title")}>
            Submission Controls
          </div>
          <CheckboxInput
            name="form_required"
            label="Form submission is required"
            description="The task cannot be completed until a form with the correct schema is associated to the task."
            isLabelShown={false}
            isFluid
            className={cx("submission-control-checkbox")}
            onChange={(value: boolean) => {
              handleValueChange("form_required", value);
            }}
          />
          <CheckboxInput
            name="autocomplete"
            label="Auto-complete upon form submission"
            description="The task automatically completes if a form with the correct schema is associated to the task."
            isLabelShown={false}
            isFluid
            className={cx("submission-control-checkbox")}
            onChange={(value: boolean) => {
              handleValueChange("autocomplete", value);
            }}
          />
          <CheckboxInput
            name="auto_attach"
            label="Auto-attach submission outside of task"
            description="If a form is submitted outside of the task but matches the schema and asset specified in the task, it will be automatically associated to the task."
            isLabelShown={false}
            isFluid
            className={cx("submission-control-checkbox")}
            onChange={(value: boolean) => {
              handleValueChange("auto_attach", value);
            }}
          />
        </div>
      </Form>
      {results.length > 0 && (
        <>
          <Banner
            titleText="The newly selected template does not account for one or more question from the existing template."
            isDismissable={false}
            variant="error"
          />
          <Card className={cx("submitFormErrors")}>
            <div className={cx("errorQuestionsTitle")}>
              Questions Not Accounted For
            </div>
            {results.map((result) => (
              <KeyValueList
                variant="shaded"
                key={result.faultyKey + result.questionName}
                data={[
                  ...(result.sectionName
                    ? [
                        {
                          title: "Section",
                          value: result.sectionName,
                        },
                      ]
                    : []),
                  { title: "Question", value: result.questionName },
                  {
                    title: "Issue",
                    value: (
                      <span className={cx("errorText")}>
                        Empty question in {result.faultyKey}
                      </span>
                    ),
                  },
                ]}
              />
            ))}
          </Card>
        </>
      )}
    </>
  );
};

export default WorkflowConfigurationSubmitForm;
